import CustomerRelationshipConsumptionBridge from './CustomerRelationshipConsumptionBridge';

import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  getMarginCenterCustomerProductActivity,
} from '../services/marginCenterService';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerProductActivityParams,
  MarginCenterCustomerProductActivitySignal,
  MarginCenterCustomerProductActivityDimensions,
  MarginCenterCustomerProductActivityFactsResponse,
} from '../types/marginCenter';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


const numberFormatter = pricingNumberFormat({
    maximumFractionDigits: 2,
  },
);


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return numberFormatter.format(value);
}


function formatSignedPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0 ? '+' : ''
  }${numberFormatter.format(value)} %`;
}


function formatDate(
  value: string | null | undefined,
): string {
  if (!value) {
    return '—';
  }

  const normalized = value.slice(0, 10);
  const [year, month, day] =
    normalized.split('-').map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  ).format(
    new Date(
      year,
      month - 1,
      day,
    ),
  );
}


function getComparisonLabel(
  value: number | null | undefined,
  suffix: string,
  comparisonMode: MarginCenterComparisonMode,
  comparisonHasNoActivity: boolean,
): string {
  if (comparisonMode === 'none') {
    return 'Sin periodo comparado';
  }

  if (comparisonHasNoActivity) {
    return 'Sin actividad en el periodo comparado';
  }

  if (value === null || value === undefined) {
    return 'Comparación no evaluable';
  }

  return `vs ${formatNumber(value)} ${suffix}`;
}


function getAttentionStyle(
  signal:
    | MarginCenterCustomerProductActivitySignal
    | undefined,
) {
  if (signal?.level === 'strong') {
    return {
      borderColor: 'rgba(245,158,11,0.34)',
      accent: '#f59e0b',
      background:
        'linear-gradient(145deg, rgba(245,158,11,0.075) 0%, rgba(15,23,42,0.52) 42%, rgba(8,17,31,0.76) 100%)',
      label: 'Cambio fuerte',
    };
  }

  if (signal?.level === 'material') {
    return {
      borderColor: 'rgba(129,140,248,0.28)',
      accent: '#a5b4fc',
      background:
        'linear-gradient(145deg, rgba(99,102,241,0.075) 0%, rgba(15,23,42,0.52) 42%, rgba(8,17,31,0.76) 100%)',
      label: 'Cambio material',
    };
  }

  if (signal?.level === 'watch') {
    return {
      borderColor: 'rgba(56,189,248,0.26)',
      accent: '#38bdf8',
      background:
        'linear-gradient(145deg, rgba(14,165,233,0.06) 0%, rgba(15,23,42,0.50) 42%, rgba(8,17,31,0.74) 100%)',
      label: 'En observación',
    };
  }

  return {
    borderColor: 'rgba(148,163,184,0.13)',
    accent: '#64748b',
    background:
      'linear-gradient(145deg, rgba(15,23,42,0.44) 0%, rgba(8,17,31,0.68) 100%)',
    label: null,
  };
}


function getSignalLabel(
  signal:
    | MarginCenterCustomerProductActivitySignal
    | undefined,
): string | null {
  if (!signal) {
    return null;
  }

  if (
    signal.family === 'cadence'
    || signal.family === 'continuity'
  ) {
    if (signal.level === 'strong') {
      return 'Alta atención';
    }

    if (signal.level === 'watch') {
      return 'En observación';
    }

    return 'Señal observada';
  }

  if (signal.level === 'strong') {
    return 'Cambio fuerte';
  }

  if (signal.level === 'material') {
    return 'Cambio material';
  }

  if (signal.level === 'watch') {
    return 'En observación';
  }

  return null;
}


function getCadenceTitle(
  state: string,
): string {
  switch (state) {
    case 'within_historical_pattern':
      return 'Compra dentro de su patrón histórico';

    case 'upper_historical_tail':
      return 'La espera actual está en el tramo alto de su cadencia';

    case 'outside_p90_historical_interval':
      return 'La espera supera su patrón histórico habitual';

    case 'insufficient_history':
      return 'Histórico insuficiente para establecer una cadencia';

    default:
      return 'Cadencia todavía no evaluable';
  }
}


function getHistoricalBandLabel(
  value: string | null | undefined,
): string | null {
  switch (value) {
    case 'within_historical_iqr':
      return 'Dentro del rango histórico central';

    case 'below_historical_p25':
      return 'Por debajo del P25 histórico';

    case 'above_historical_p75':
      return 'Por encima del P75 histórico';

    default:
      return null;
  }
}


function describeActivity(
  dimensions: MarginCenterCustomerProductActivityDimensions,
  facts: MarginCenterCustomerProductActivityFactsResponse,
  comparisonMode: MarginCenterComparisonMode,
): { headline: string; accompanying: string | null; cadence: string } {
  const currentDays = facts.current?.purchase_days ?? null;
  const previousDays = facts.comparison?.purchase_days ?? null;
  let headline: string;
  let accompanying: string | null = null;

  if (currentDays === null) {
    headline = 'No hay datos suficientes para describir las compras del periodo.';
  } else if (currentDays === 0) {
    headline = 'No hay compras de este producto en el periodo analizado.';
    if (comparisonMode !== 'none' && previousDays !== null && previousDays > 0) {
      accompanying = 'Sí hubo compras en el periodo comparado.';
    }
  } else if (comparisonMode === 'none') {
    headline = 'Hay actividad de compra en el periodo seleccionado.';
    accompanying = 'Sin comparación temporal: mostramos el comportamiento observado.';
  } else if (previousDays === 0) {
    headline = 'Hay compras ahora, frente a un periodo comparado sin actividad.';
    accompanying = 'No existe una base anterior de compra para expresar el cambio en porcentaje.';
  } else if (previousDays === null) {
    headline = 'Hay compras en el periodo, pero no se puede completar la comparación.';
  } else {
    switch (dimensions.consumption_rate.state) {
      case 'increase':
        headline = 'El ritmo de compra aumenta frente al periodo comparado.';
        break;
      case 'decrease':
        headline = 'El ritmo de compra disminuye frente al periodo comparado.';
        break;
      case 'no_material_change':
        headline = 'El ritmo de compra presenta cambios pequeños frente al periodo comparado.';
        break;
      default:
        headline = 'Hay actividad de compra; el cambio de ritmo no es evaluable.';
    }
    const observations: string[] = [];
    switch (dimensions.purchase_frequency.state) {
      case 'increase': observations.push('Compra en más días por cada 30 días'); break;
      case 'decrease': observations.push('Compra en menos días por cada 30 días'); break;
      case 'no_material_change': observations.push('La frecuencia apenas varía'); break;
      default: break;
    }
    switch (dimensions.purchase_size.state) {
      case 'increase': observations.push('adquiere más unidades por día con compra'); break;
      case 'decrease': observations.push('adquiere menos unidades por día con compra'); break;
      case 'no_material_change': observations.push('el tamaño medio de compra apenas varía'); break;
      default: break;
    }
    if (observations.length > 0) {
      const sentence = observations.join(' y ');
      accompanying = `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
    }
  }

  let cadence: string;
  switch (dimensions.cadence.state) {
    case 'within_historical_pattern':
      cadence = 'La espera desde la última compra se mantiene dentro de su patrón histórico.';
      break;
    case 'upper_historical_tail':
      cadence = 'La espera desde la última compra está en la parte alta de su histórico.';
      break;
    case 'outside_p90_historical_interval':
      cadence = 'La espera desde la última compra supera su referencia histórica alta.';
      break;
    case 'insufficient_history':
      cadence = 'Todavía no hay suficientes compras para establecer su cadencia habitual.';
      break;
    default:
      cadence = 'No es posible valorar la cadencia con los datos disponibles.';
  }
  return { headline, accompanying, cadence };
}


function CompactActivityMetric({
  label,
  value,
  comparison,
  delta,
  signal,
  contextLabel,
}: {
  label: string;
  value: string;
  comparison: string;
  delta: number | null;
  signal?: MarginCenterCustomerProductActivitySignal;
  contextLabel?: string | null;
}) {
  const attention = getAttentionStyle(signal);
  const signalLabel = getSignalLabel(signal);
  return (
    <Box sx={{ minWidth: 0, p: 1.15, borderRadius: 1.6, bgcolor: 'rgba(15,23,42,0.46)', border: `1px solid ${signal ? attention.borderColor : 'rgba(148,163,184,0.13)'}` }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.6}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 750 }}>
          {label}
        </Typography>
        {signalLabel && (
          <Chip size="small" label={signalLabel} sx={{ height: 19, color: attention.accent, fontWeight: 800, bgcolor: 'rgba(2,6,23,0.23)', border: `1px solid ${attention.borderColor}`, '& .MuiChip-label': { px: 0.65, fontSize: 10 } }} />
        )}
      </Stack>
      <Typography variant="h6" sx={{ mt: 0.2, fontWeight: 900, color: '#f8fafc', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.3 }}>
        {comparison}
      </Typography>
      {delta !== null && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.2, fontWeight: 850, color: delta > 0 ? '#34d399' : delta < 0 ? '#fb7185' : '#94a3b8' }}>
          {formatSignedPct(delta)}
        </Typography>
      )}
      {contextLabel && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#94a3b8' }}>
          {contextLabel}
        </Typography>
      )}
    </Box>
  );
}

function CadenceVisual({
  dimension,
  lastPurchaseDate,
  completedIntervals,
  signal,
}: {
  dimension: MarginCenterCustomerProductActivityDimensions['cadence'];
  lastPurchaseDate: string | null;
  completedIntervals: number | null;
  signal?: MarginCenterCustomerProductActivitySignal;
}) {
  const elapsed = dimension.days_since_last_purchase;
  const median = dimension.median_interval_days;
  const p75 = dimension.p75_interval_days;
  const p90 = dimension.p90_interval_days;
  const visualAvailable = dimension.state !== 'insufficient_history'
    && dimension.state !== 'not_evaluable'
    && elapsed !== null && Number.isFinite(elapsed) && elapsed >= 0
    && median !== null && p75 !== null && p90 !== null
    && Number.isFinite(median) && Number.isFinite(p75) && Number.isFinite(p90)
    && median >= 0 && p75 >= median && p90 >= p75 && p90 > 0;
  const max = visualAvailable ? Math.max(p90!, elapsed!, 1) : 1;
  const position = (value: number) => `${Math.min(99, Math.max(1, value / max * 100))}%`;
  const attention = getAttentionStyle(signal);
  const signalLabel = getSignalLabel(signal);
  return (
    <Box sx={{ mt: 1.15, p: 1.25, borderRadius: 1.7, bgcolor: 'rgba(15,23,42,0.40)', border: `1px solid ${signal ? attention.borderColor : 'rgba(56,189,248,0.20)'}` }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} useFlexGap flexWrap="wrap">
        <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 850 }}>Cadencia de compra</Typography>
        {signalLabel && <Chip size="small" label={signalLabel} sx={{ height: 20, color: attention.accent, border: `1px solid ${attention.borderColor}`, bgcolor: 'rgba(2,6,23,0.23)', fontWeight: 800 }} />}
      </Stack>
      <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.25 }}>
        {getCadenceTitle(dimension.state)}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" sx={{ mt: 0.55 }}>
        <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
          {elapsed === null ? '—' : `${formatNumber(elapsed)} días`}
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          desde última compra hasta el final del periodo
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.25 }}>
        Última compra: {formatDate(lastPurchaseDate)}
      </Typography>
      {visualAvailable ? (
        <>
          <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', mt: 0.95, mb: 0.6, fontWeight: 750 }}>
            Espera actual frente a sus intervalos históricos de compra
          </Typography>
          <Box role="img" aria-label={`Espera actual ${formatNumber(elapsed)} días. Mediana histórica ${formatNumber(median)} días, percentil 75 ${formatNumber(p75)} días y percentil 90 ${formatNumber(p90)} días.`} sx={{ position: 'relative', mt: 1.15, mb: 1.15, height: 17 }}>
            <Box sx={{ height: 10, borderRadius: 3, bgcolor: 'rgba(100,116,139,0.30)', overflow: 'hidden' }}>
              <Box sx={{ width: position(p75!), height: '100%', bgcolor: 'rgba(56,189,248,0.20)' }} />
            </Box>
            {[median!, p75!, p90!].map((value, i) => (
              <Box key={i} sx={{ position: 'absolute', top: -3, height: 16, left: position(value), borderLeft: '1px solid #94a3b8' }} />
            ))}
            <Box sx={{ position: 'absolute', left: position(elapsed!), top: -6, transform: 'translateX(-50%)', height: 22, width: 3, borderRadius: 2, bgcolor: '#34d399', boxShadow: '0 0 0 2px rgba(5,18,31,0.9)' }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.7 }}>
            {[
              ['Mediana', median!], ['P75', p75!], ['P90', p90!],
            ].map(([label, value]) => (
              <Box key={String(label)}>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
                <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 850 }}>{formatNumber(Number(value))} días</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.65 }}>
            La barra marca días, no un percentil calculado de la espera actual. P75 y P90 son referencias de los intervalos entre compras.
          </Typography>
        </>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.8 }}>
          No hay suficientes referencias históricas calculables para dibujar la escala de cadencia.
        </Typography>
      )}
      {completedIntervals !== null && (
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.45 }}>
          {formatNumber(completedIntervals)} intervalos históricos completos observados.
        </Typography>
      )}
    </Box>
  );
}


export default function CustomerRelationshipActivityPanel({
  customerId,
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
}: {
  customerId: string;
  productId: string;
  startDate: string | null;
  endDate: string | null;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string | null;
  comparisonEndDate: string | null;
}) {
  const params =
    useMemo<MarginCenterCustomerProductActivityParams>(
      () => ({
        start_date: startDate ?? undefined,
        end_date: endDate ?? undefined,
        comparison_mode: comparisonMode,
        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,
        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
      ],
    );

  const activityQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-product-activity',
      customerId,
      productId,
      params,
    ],
    queryFn: () =>
      getMarginCenterCustomerProductActivity(
        customerId,
        productId,
        params,
      ),
  });

  const response = activityQuery.data;

  const dimensions =
    response?.status === 'ok'
      ? response.dimensions
      : null;

  const facts =
    response?.status === 'ok'
      ? response.activity
      : null;

  const signals =
    response?.status === 'ok'
      ? response.signals
      : [];

  const cadenceSignal =
    signals.find(
      (signal) => signal.family === 'cadence',
    );

  const consumptionSignal =
    signals.find(
      (signal) =>
        signal.family === 'consumption_rate',
    );

  const frequencySignal =
    signals.find(
      (signal) =>
        signal.family === 'purchase_frequency',
    );

  const purchaseSizeSignal =
    signals.find(
      (signal) =>
        signal.family === 'purchase_size',
    );

  const continuitySignal =
    signals.find(
      (signal) =>
        signal.family === 'continuity',
    );

  const continuityStyle =
    getAttentionStyle(continuitySignal);

  const comparisonHasNoActivity =
    comparisonMode !== 'none'
    && facts?.comparison?.purchase_days === 0;

  const historicalBandLabel =
    getHistoricalBandLabel(
      dimensions?.purchase_size.historical_band,
    );
  const plainReading = dimensions && facts
    ? describeActivity(dimensions, facts, comparisonMode)
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border:
          '1px solid rgba(56,189,248,0.15)',
        background:
          'linear-gradient(160deg, rgba(14,165,233,0.055) 0%, rgba(15,23,42,0.55) 28%, rgba(8,17,31,0.82) 100%)',
        boxShadow:
          '0 14px 34px rgba(2,6,23,0.16)',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {activityQuery.isFetching && (
        <LinearProgress
          sx={{
            height: 2,
            bgcolor:
              'rgba(56,189,248,0.05)',
          }}
        />
      )}

      <Box sx={{ p: 1.65 }}>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              Actividad y comportamiento de compra
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: '#64748b' }}
            >
              Cadencia, ritmo, frecuencia y tamaño de compra.
            </Typography>
          </Box>

          {response?.status === 'ok' && (
            <Stack
              direction="row"
              spacing={0.65}
              useFlexGap
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={
                  facts?.cadence?.sample_status
                    === 'established'
                    ? 'Patrón histórico establecido'
                    : 'Histórico limitado'
                }
                sx={{
                  height: 23,
                  color: '#94a3b8',
                  border:
                    '1px solid rgba(148,163,184,0.17)',
                  bgcolor:
                    'rgba(15,23,42,0.38)',
                  '& .MuiChip-label': {
                    fontSize: 10.5,
                    fontWeight: 800,
                  },
                }}
              />

              <Chip
                size="small"
                label={
                  `${response.summary.signal_count} ${
                    response.summary.signal_count === 1
                      ? 'señal'
                      : 'señales'
                  }`
                }
                sx={{
                  height: 23,
                  color: '#cbd5e1',
                  border:
                    '1px solid rgba(148,163,184,0.17)',
                  bgcolor:
                    'rgba(15,23,42,0.38)',
                  '& .MuiChip-label': {
                    fontSize: 10.5,
                    fontWeight: 800,
                  },
                }}
              />
            </Stack>
          )}
        </Stack>

        {activityQuery.isError && (
          <Alert
            severity="info"
            sx={{ mt: 1.5 }}
          >
            No se pudo cargar el análisis de actividad de esta
            relación.
          </Alert>
        )}

        {response
          && response.status !== 'ok'
          && !activityQuery.isError
          && (
            <Alert
              severity="info"
              sx={{ mt: 1.5 }}
            >
              Todavía no existe histórico suficiente para analizar
              el comportamiento de compra de esta relación.
            </Alert>
          )}

        {dimensions && facts && plainReading && (
          <>
            <Box sx={{ mt: 1.35, p: 1.35, borderRadius: 2, bgcolor: 'rgba(22,46,65,0.60)', border: '1px solid rgba(56,189,248,0.24)' }}>
              <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 850, lineHeight: 1.2 }}>
                Lectura comercial · hechos observados
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 0.35, color: '#f8fafc', fontWeight: 850 }}>
                {plainReading.headline}
              </Typography>
              {plainReading.accompanying && (
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.55 }}>
                  {plainReading.accompanying}
                </Typography>
              )}
            </Box>

            <CustomerRelationshipConsumptionBridge
              facts={facts}
              selection={response?.selection ?? null}
              comparisonMode={comparisonMode}
              lastAvailableDate={facts.dataset?.last_transaction_date ?? null}
            />

            <CadenceVisual
              dimension={dimensions.cadence}
              lastPurchaseDate={facts.recency?.last_purchase_date ?? null}
              completedIntervals={facts.cadence?.completed_interval_count ?? null}
              signal={cadenceSignal}
            />

            {continuitySignal && (
              <Box sx={{ mt: 0.9, px: 1.1, py: 0.8, borderRadius: 1.4, border: `1px solid ${continuityStyle.borderColor}`, bgcolor: 'rgba(15,23,42,0.38)' }}>
                <Typography variant="caption" sx={{ color: continuityStyle.accent, fontWeight: 850 }}>
                  Continuidad de compra · {getSignalLabel(continuitySignal) ?? 'Señal observada'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.2 }}>
                  {continuitySignal.code === 'purchase_activity_present_after_empty_comparison'
                    ? 'Hay compras en el periodo actual, frente a un periodo comparado sin actividad.'
                    : continuitySignal.code === 'no_purchase_activity_in_current_period'
                      ? 'No hay compras en el periodo actual, aunque sí hubo en el comparado.'
                      : 'La presencia de compras cambia entre los periodos seleccionados.'}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 1.15, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
              <CompactActivityMetric
                label="Ritmo · unidades cada 30 días"
                value={`${formatNumber(dimensions.consumption_rate.current_units_per_30_days)} uds.`}
                comparison={getComparisonLabel(dimensions.consumption_rate.comparison_units_per_30_days, 'uds. / 30 días', comparisonMode, comparisonHasNoActivity)}
                delta={comparisonMode === 'none' || comparisonHasNoActivity ? null : dimensions.consumption_rate.delta_pct}
                signal={consumptionSignal}
              />
              <CompactActivityMetric
                label="Frecuencia · días con compra cada 30 días"
                value={`${formatNumber(dimensions.purchase_frequency.current_purchase_days_per_30_days)} días`}
                comparison={getComparisonLabel(dimensions.purchase_frequency.comparison_purchase_days_per_30_days, 'días / 30 días', comparisonMode, comparisonHasNoActivity)}
                delta={comparisonMode === 'none' || comparisonHasNoActivity ? null : dimensions.purchase_frequency.delta_pct}
                signal={frequencySignal}
              />
              <CompactActivityMetric
                label="Tamaño · unidades por día con compra"
                value={`${formatNumber(dimensions.purchase_size.current_average_units_per_purchase_day)} uds.`}
                comparison={getComparisonLabel(dimensions.purchase_size.comparison_average_units_per_purchase_day, 'uds. / día con compra', comparisonMode, comparisonHasNoActivity)}
                delta={comparisonMode === 'none' || comparisonHasNoActivity ? null : dimensions.purchase_size.delta_pct}
                signal={purchaseSizeSignal}
                contextLabel={historicalBandLabel}
              />
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: '#64748b' }}>
              Ritmo y frecuencia normalizados a 30 días; tamaño medio por día con compra. {formatNumber(facts.history?.purchase_days)} días de compra en el histórico de esta relación.
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
}